import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slice/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import report from '../../../../utils/api/report';

interface ChartDataProperties {
  label: string;
  scheduledPowerConsumed: number;
  guestPowerConsumed: number;
  [key: string]: string | number; // Add this to allow dynamic properties
}

/**
 *  Energy report duration chart
 */
function EnergyReportDurationChart() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('scheduleCharging')} {ToFixedValue(data?.scheduledPowerConsumed || 0)} kwh
            </p>
            <p style={{ color: '#2AA700' }}>
              {t('guestCharging')} {ToFixedValue(data?.guestPowerConsumed || 0)} kwh
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'powerConsumedByGuestVsSchedule',
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response.data).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              scheduledPowerConsumed: (item as any).scheduledPowerConsumed / 1000 || 0,
              guestPowerConsumed: (item as any).guestPowerConsumed / 1000 || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('powerConsumedByGuestVsSchedule') ? 'active' : ''}`}
              onClick={() => handleClick('powerConsumedByGuestVsSchedule')}>
              {reportsData.activePinName.includes('powerConsumedByGuestVsSchedule') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('energyConsumptionGuestVsScheduled')}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) fetchPowerConsumedReport(selected.value);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData} barCategoryGap={25}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                fontSize={12}
                stroke="#535353"
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="scheduledPowerConsumed" stackId="a" barSize={26} fill="#4ABDFB" />
              <Bar dataKey="guestPowerConsumed" stackId="a" barSize={26} fill="#2AA700" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#4ABDFB' }} />
          <p>{t('scheduleCharging')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
          <p>{t('guestCharging')}</p>
        </div>
      </div>
    </div>
  );
}

export default EnergyReportDurationChart;
